import { Injectable } from '@angular/core';
import { PlayConfigurationModel } from './models/animation/play-configuration.model';
import { TimerangeModel } from './models/animation/timerange.model';
import { plainToClass } from 'class-transformer';
import { get } from 'lodash';
import { SetConfigurationModel } from './models/animation/set-configuration.model';
import { ChapterModel } from '../story/models/chapter.model';
import { TimelineMapModel } from '../story/models/timeline-map.model';
import { LeafTimelineModel } from './models/animation/leaf-timeline.model';
import { BranchTimelineModel } from './models/animation/branch-timeline.model';
import { TimelineMapUpdate } from '../edit/models/timeline-map-update.model';

declare const Human: any;

@Injectable({
  providedIn: 'root'
})
export class AnimationService {
  playParams: PlayConfigurationModel; 
  fps = 30;

  constructor(
  ) { }

  getChapterLocalTimerange(chapterId: string): TimerangeModel {
    const chapter = Human.timeline.activeRoot._chapters.find(_chapter => _chapter.id === chapterId);
    return plainToClass(TimerangeModel, chapter.timeRange as object);

    // Human.timeline.activeRoot._nowBranch;
    // get branch timeline:               <branch>.getTimeframe()
    // convert local to globalTime:       Human.timeline.activeRoot.mapBranchTime(<branch>, 1);
    // scrub animation using globalTime:  Human.timeline.scrub(globalTime):  
  }

  mapGlobalTimeToLocal(time: number, params = {}): {branch: BranchTimelineModel, time: number, index: number} {
    const localTime = Human.timeline.activeRoot.mapGlobalTime(time, params);

    // BUG: When globalTime is zero, localTime is -3
    if (localTime.time < 0) {
      localTime.time = 0;
    }

    return localTime;
  }

  mapLocalTimeToGlobal(chapterId: string, time: number): number {
    const branch = Human.timeline.activeRoot._chapters.find(chapter => chapter.id === chapterId);
    return Human.timeline.activeRoot.mapBranchTime(branch, time);
  }

  // Like mapGlobalTimeToLocal but with the added logic of choosing the
  // Current chapter if the time is at that chapter's start. 
  getLocalTime (globalTime: number): number {
    const params = { tie: '' };

    const currentBranch = Human.timeline.activeRoot._nowBranch;
    const firstTime = currentBranch.timeRange.firstTime;

    const startTime = this.mapLocalTimeToGlobal(currentBranch.id, firstTime);

    params.tie = globalTime === startTime ? 'later' : 'earlier';

    return this.mapGlobalTimeToLocal(globalTime, params).time;
  }

  isChapterAnimated(chapter) {
    const isAnimated = chapter.timelineMap
      .filter(timeline => {
        return get(timeline, 'data.timeline') !== 'dummy' &&
          get(timeline, 'data.timeline.id') !== 'dummy' &&
          get(timeline, 'data.timeline.id.id') !== 'dummy';
      }).length > 0;

    return isAnimated;  
  }

  isChapterLooped(chapter: ChapterModel) {
    if (get(chapter, 'loop') === true) {
      return true;
    }

    if (get(chapter, 'ui.play.count') === -1) {
      return true;
    }

    return false;
  }

  scrub(options: SetConfigurationModel) {
    const which = options.which ? options.which : 'first';
    Human.timeline.scrub({
      time: options.time,
      borderCondition: {
        which: which,
        direction: 'forward'
      }
    });

  }

  scrubFrame (directionName: 'next' | 'previous') {
    const direction = directionName === 'next' ? 1 : -1;

    // Do the addition in terms of whole frames in order to avoid
    // The accumulation of floating point rounding errors
    const currentTime = this.getTime();
    const currentFrame = this.secondsToFrames(currentTime);
    const nextFrame = currentFrame + direction;
    const nextTime = this.framesToSeconds(nextFrame);

    // Constraints
    const currentBranch = Human.timeline.activeRoot._nowBranch;
    const timeRange = currentBranch.timeRange;

    const startTime = this.mapLocalTimeToGlobal(currentBranch.id, timeRange.firstTime);
    
    const endTime = this.mapLocalTimeToGlobal(currentBranch.id, timeRange.lastTime);

    let scrubConfig;

    if (nextTime <= startTime) {

      scrubConfig = { time: startTime, which: 'second' };

    } else if (nextTime >= endTime) {

      scrubConfig = { time: endTime, which: 'first' };

    } else {

      scrubConfig = { time: nextTime };

    }
    
    this.scrub(scrubConfig);
  }

  // Converts an engine TimelineMap instance to the app's
  // TimelineMapModel
  fromEngineTimelineMap(timelineMap): TimelineMapModel {
    return {
      id: timelineMap.id,
      name: timelineMap.name,
      start: timelineMap.start,
      end: timelineMap.end,
      data: {
        timeline: timelineMap.leafTimeline.id,
        start: timelineMap.dataStart,
        end: timelineMap.dataEnd,
        fit: timelineMap.fit,
        onEnd: timelineMap.onEnd
      }
    }; 
  }

  secondsToFrames (seconds: number): number {
    return Math.round(seconds * this.fps);
  }

  framesToSeconds (frames: number): number {
    return frames / this.fps;
  }

  toNearestFrame (seconds: number): number {
    return this.framesToSeconds( this.secondsToFrames(seconds) );
  }

  // Convert time in seconds to a string in the form: minutes:seconds:frames
  formatTime (time: number): string {

    const formatMinutesSeconds = (input: number): string => {
      const roundFunc = time < 0 ? Math.ceil : Math.floor;
      const result = Math.abs(roundFunc(input));

      return result.toString().padStart(2, '0');
    };

    const formatFrames = (input: number): string => {
      const result = Math.abs(this.secondsToFrames(input % 1));
      return result.toString().padStart(2, '0');
    };
    
    const minutes = formatMinutesSeconds(time / 60);
    const seconds = formatMinutesSeconds(time % 60);
    const frames = formatFrames(time % 1);

    const sign = time < 0 ? '-' : '';

    return `${sign}${minutes}:${seconds}:${frames}`;
  }

  // Convert string in the form: minutes:seconds:frames to time in seconds
  parseFormattedTime (formattedTime: string): number {
    const signExp = /^-/;

    const sign = signExp.test(formattedTime) ? -1 : 1;

    if (sign === -1) {
      formattedTime = formattedTime.replace(signExp, '');
    }

    const timeParts = formattedTime.split(':').reverse();

    let frames = parseInt(timeParts[0], 10);
    let seconds = parseInt(timeParts[1], 10);
    let minutes = parseInt(timeParts[2], 10);

    if (isNaN(frames)) {
      frames = 0;
    }

    if (isNaN(seconds)) {
      seconds = 0;
    }

    if (isNaN(minutes)) {
      minutes = 0;
    }

    return ((minutes * 60) + seconds + this.framesToSeconds(frames)) * sign;
  }

  // Constrain a given time to the range of the current chapter
  constrainLocalTime (time: number): number {
    const timeRange = Human.timeline.activeRoot._nowBranch.timeRange;

    if (time < timeRange.firstTime) {
      time = timeRange.firstTime;
    }

    if (time > timeRange.lastTime) {
      time = timeRange.lastTime;
    }

    return time;
  }

  // Add TimelineMap to engine and returned newly id'd model.
  addTimelineMap (branch: BranchTimelineModel, timelineMap: TimelineMapModel): TimelineMapModel {

    const currentTimeFrame = branch.getTimeFrame();

    const id = branch.createTimelineMap(timelineMap);
    timelineMap.id = id;

    const newTimeFrame = branch.getTimeFrame();

    // Rebuild the root if the addition changes the branch's timeframe.
    if (currentTimeFrame.lastTime !== newTimeFrame.lastTime) {
      Human.timeline.activeRoot.build();
    }

    return timelineMap;
  }

  updateTimelineMaps (branch: BranchTimelineModel, updates: TimelineMapUpdate[]) {
    const currentTimeFrame = branch.getTimeFrame();

    updates.forEach((update) => {
      branch.updateTimelineMap(update.id, update);
    });

    const newTimeFrame = branch.getTimeFrame();

    // Rebuild the root if the update changes the branch's timeframe.
    if (currentTimeFrame.lastTime !== newTimeFrame.lastTime) {
      Human.timeline.activeRoot.build();
    }
  }

  deleteTimelineMaps (branch: BranchTimelineModel, timelineMapIds: string[]) {
    const currentTimeFrame = branch.getTimeFrame();

    timelineMapIds.forEach((timelineMapId) => {
      branch.deleteTimelineMap(timelineMapId);
    });

    const newTimeFrame = branch.getTimeFrame();

    // Rebuild the root if the update changes the branch's timeframe.
    if (currentTimeFrame.lastTime !== newTimeFrame.lastTime) {
      Human.timeline.activeRoot.build();
    }
  }

  getLeafTimelines(): { [id: string]: LeafTimelineModel } {
    return Human.timeline.leafTimelines;
  }

  getTimeFrame(): TimerangeModel {
    return Human.timeline.getTimeFrame();
  }

  getTime(): number {
    return Human.timeline.time;
  }

  isPlaying(): boolean {
    return Human.timeline.playing;
  }

  hasChapterFinished(chapterId: string): boolean {
    const timerange = this.getChapterLocalTimerange(chapterId);
    const localTime = this.mapGlobalTimeToLocal(Human.timeline.time);
    
    return (localTime.time === timerange.lastTime);
  }

  play(options: PlayConfigurationModel) {
    // spread props, but filter any that are undefined.
    // The engine does `hasOwnProperty` checks, so `undefined` params can slip through the cracks if not careful
    this.playParams = Object.keys(options).reduce<PlayConfigurationModel>(
      (acc, option) => {
        if (acc[option] === undefined) {
          delete acc[option];
        }
        return acc;
      },
      { ...options }
    );
    
    Human.timeline.play( this.playParams );
  }

  rewind(chapterId: string) {
    const resetTime = this.mapLocalTimeToGlobal(chapterId, 0);
    this.scrub({time: resetTime, which: 'second'});
  }

  unpause(chapterId: string) {
    // If the user unpauses at the end of an animation, it starts playing the next chapter, so this rewinds first
    if (this.hasChapterFinished(chapterId)) {
      this.rewind(chapterId);
    }

    Human.timeline.unpause();
  }

  pause() {
    Human.timeline.pause();
  }

  // Human.timeline.scrub({
    //   time: 1
    // });

    // Human.timeline.activeRoot._nowBranch;
    // Human.timeline.activeRoot.mapGlobalTime(Human.timeline.time);

    // Sequence of events:
    // Human.timeline.activeRoot._nowBranch;
    // get branch timeline:               <branch>.getTimeframe()
    // convert local to globalTime:       Human.timeline.activeRoot.mapBranchTime(<branch>, 1);
    // scrub animation using globalTime:  Human.timeline.scrub(globalTime):  

    // Border conditions:
    // Human.timeline.borderConditions (both end of chapter and start of another)
    // Human.timeline.scrub({ time: 7, borderCondition: { which: 'second', direction: 'forward' }}) == start
    // Human.timeline.scrub({ time: 7, borderCondition: { which: 'first', direction: 'forward' }}) == end

    // Events
    // timeline.scrubbed (globalTime)
    // timeline.play

    // Playing (start conditions, end conditions, play conditions)
    // Human.timeline.play({ startChapterId: ..., numChapters: 1, loop: true })

}
